import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useMobileEmbed } from "@/hooks/useMobileEmbed";
import { ArrowLeft } from "lucide-react";

function BulletList({ items }) {
  return (
    <ul className="mt-3 list-disc space-y-2 pl-5">
      {items.map((item) => (
        <li key={item} className="text-sm leading-relaxed text-zinc-600">
          {item}
        </li>
      ))}
    </ul>
  );
}

function PolicySection({ section, contactEmail }) {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold text-zinc-900">{section.title}</h2>
      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-relaxed text-zinc-600">
          {paragraph}
        </p>
      ))}
      {section.subsections?.map((subsection) => (
        <div key={subsection.title} className="mt-4">
          <h3 className="text-sm font-semibold text-zinc-800">{subsection.title}</h3>
          <BulletList items={subsection.items} />
        </div>
      ))}
      {section.listItems && <BulletList items={section.listItems} />}
      {section.closing?.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-relaxed text-zinc-600">
          {paragraph}
        </p>
      ))}
      {section.contact && (
        <p className="mt-3 text-sm leading-relaxed text-zinc-600">
          <a
            href={`mailto:${contactEmail}`}
            className="font-medium text-[color:var(--sentinel)] hover:underline"
          >
            {contactEmail}
          </a>
        </p>
      )}
    </section>
  );
}

export default function LegalDocumentPage({ content, testId, htmlLang }) {
  const isMobileEmbed = useMobileEmbed();

  useEffect(() => {
    document.title = `${content.pageTitle} · Cyrqle`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", content.metaDescription);
    if (htmlLang) {
      document.documentElement.lang = htmlLang;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [content, htmlLang]);

  return (
    <div className="min-h-screen bg-white">
      {!isMobileEmbed && <Header />}
      <main
        className={`container-x max-w-3xl ${isMobileEmbed ? "py-6 md:py-8" : "py-10 md:py-14"}`}
        data-testid={testId}
      >
        {!isMobileEmbed && (
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 transition hover:text-[color:var(--sentinel)]"
          >
            <ArrowLeft className="h-4 w-4" />
            {content.back}
          </Link>
        )}

        <h1
          className={`font-display text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl ${
            isMobileEmbed ? "" : "mt-6"
          }`}
        >
          {content.pageTitle}
        </h1>
        <p className="mt-3 text-sm text-zinc-500">{content.lastUpdated}</p>
        <p className="mt-6 text-sm leading-relaxed text-zinc-600">{content.intro}</p>

        <div className="mt-10 space-y-10">
          {content.sections.map((section) => (
            <PolicySection
              key={section.title}
              section={section}
              contactEmail={content.contactEmail}
            />
          ))}
        </div>
      </main>
      {!isMobileEmbed && <Footer />}
    </div>
  );
}
